import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, toast } from "@sagot/ui";
import { useId, useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";

const TAG_MAX = 40;
const TAGS_MAX = 20;

/** Normalized tag list after adding `raw` (trimmed, lower-case, unique, capped). */
export function addTag(tags: readonly string[], raw: string): string[] {
  const tag = raw.trim().toLowerCase().slice(0, TAG_MAX);
  if (!tag || tags.includes(tag) || tags.length >= TAGS_MAX) return [...tags];
  return [...tags, tag];
}

interface TagEditorProps {
  tags: readonly string[];
  editable: boolean;
  onChange: (tags: string[]) => Promise<unknown>;
}

/** Tag chips + inline input (Enter adds, × removes); saved through `PATCH /v1/conversations/{id}`. */
export function TagEditor({ tags, editable, onChange }: TagEditorProps) {
  const { t } = useTranslation();
  const id = useId();
  const errorMessage = useErrorMessage();
  const [value, setValue] = useState("");

  const save = (next: string[], message: string) => {
    onChange(next).then(
      () => toast.success(message),
      (error: unknown) => toast.error(errorMessage(error)),
    );
  };

  return (
    <div>
      <label className="label" htmlFor={id}>
        {t("engage.inbox.details.tags")}
      </label>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-line-strong p-1.5">
        {tags.map((tag) => (
          <Badge key={tag} tone="brand">
            {tag}
            {editable ? (
              <button type="button" className="-m-1.5 inline-flex size-6 items-center justify-center rounded-pill hover:text-fg focus-visible:outline-offset-0" aria-label={t("engage.inbox.details.removeTag", { tag })} onClick={() => { save(tags.filter((x) => x !== tag), t("engage.inbox.details.tagRemoved")); }}>
                <Icon name="x" className="size-3" />
              </button>
            ) : null}
          </Badge>
        ))}
        <input
          id={id}
          className="-my-0.5 h-6 min-w-20 flex-1 bg-transparent px-1 text-xs outline-none placeholder:text-fg-subtle disabled:cursor-not-allowed"
          placeholder={t("engage.inbox.details.addTag")}
          maxLength={TAG_MAX}
          disabled={!editable}
          value={value}
          onChange={(e) => { setValue(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || !value.trim()) return;
            e.preventDefault();
            const next = addTag(tags, value);
            setValue("");
            if (next.length !== tags.length) save(next, t("engage.inbox.details.tagAdded"));
          }}
        />
      </div>
    </div>
  );
}
